// src/components/SalonDisplayer.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SalonDisplayer.scss';
import { Mesa, Salon, Objeto } from '../../../types';
import ItemControllers from './components/ItemControllers/ItemControllers';
import MesaController from './components/MesaController/MesaController';
import {
  handleKeyPress as handleKeyPressRaw,
  handleDragStart,
  handleDrop
} from './components/functions/listeners';
import { updateSalones } from './components/functions/updateSalones';
import PedidosListener from '../../../../Pedidos/Common/PedidosListener/PedidosListener';
import RenderMesas from './components/RenderMesas/RenderMesas';
import RenderObjetos from './components/RenderObjetos/RenderObjetos';
import ItemControllerMobile from './components/ItemControllers/ItemControllerMobile';
import { ReservationDetails, DisplayPrefs } from '../../../../Pedidos/Restaurant/types';

interface SalonesDisplayerProps {
  salones: Salon[];
  selectedSalon: Salon | undefined;
  selectedItem: Mesa | Objeto | null;
  setSelectedItem: (item: Mesa | Objeto | null) => void;
  setSelectedSalon: (salon: Salon | undefined) => void;
  setSalones: (salones: Salon[]) => void;
  actionType: 'restaurant' | 'reservation' | 'configuration';
  isPedidos: boolean;
  scaleFactor: number;               // viene del padre
  setScaleFactor?: (n: number) => void; // ya no se usa aquí
  mergedMesas?: any[];
  mergeMode?: boolean;
  setMergedMesas?: (mesas: Mesa[]) => void;
  filteredReservationsInAHour?: ReservationDetails[];
  displayPrefs?: DisplayPrefs;
  hosterMode?: boolean;
}

const SalonesDisplayer: React.FC<SalonesDisplayerProps> = ({
  salones,
  selectedSalon,
  selectedItem,
  setSelectedItem,
  setSelectedSalon,
  setSalones,
  actionType,
  isPedidos,
  scaleFactor,
  mergedMesas = [],
  mergeMode = false,
  setMergedMesas = () => {},
  filteredReservationsInAHour = [],
  displayPrefs,
  hosterMode = false,
}) => {
  const [showMesaModal, setShowMesaModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const gridRef = useRef<HTMLDivElement>(null);
  const [pxPerCellX, setPxPerCellX] = useState<number>(18);
  const [pxPerCellY, setPxPerCellY] = useState<number>(32);
  
  // Boundary warning state
  const [showBoundaryWarning, setShowBoundaryWarning] = useState(false);
  const [boundaryWarningMessage, setBoundaryWarningMessage] = useState('');
  const [gridHasViolation, setGridHasViolation] = useState(false);

  // Function to show boundary warning
  const displayBoundaryWarning = useCallback((message: string) => {
    setBoundaryWarningMessage(message);
    setShowBoundaryWarning(true);
    setGridHasViolation(true);
    
    // Hide warning after 3 seconds
    setTimeout(() => {
      setShowBoundaryWarning(false);
      setGridHasViolation(false);
    }, 3000);
  }, []);

  // Check for boundary violations when selectedItem changes
  // COMMENTED OUT: 50% boundary checking disabled - needs fixing
  // useEffect(() => {
  //   if (!selectedItem || !gridRef.current || actionType !== 'configuration') {
  //     setGridHasViolation(false);
  //     return;
  //   }

  //   const gridRect = gridRef.current.getBoundingClientRect();
  //   // The issue is that pxPerCellX/Y are not the right scaling factors for position
  //   // We need to calculate the actual pixel position based on the grid size
  //   // Looking at RenderMesas, positions are calculated differently than dimensions
  //   
  //   // Calculate actual position in pixels based on grid proportions
  //   const gridCellsX = 30; // Approximate grid cells horizontally (you can adjust)
  //   const gridCellsY = 30; // Approximate grid cells vertically (you can adjust)
  //   const pixelsPerGridCellX = gridRect.width / gridCellsX;
  //   const pixelsPerGridCellY = gridRect.height / gridCellsY;
  //   
  //   const itemX = selectedItem.x_pos * pixelsPerGridCellX;
  //   const itemY = selectedItem.y_pos * pixelsPerGridCellY;
  //   
  //   // For dimensions, use the actual rendered size from pxPerCell (which is correct for dimensions)
  //   const itemWidthPx = selectedItem.width * pxPerCellX;
  //   const itemHeightPx = selectedItem.height * pxPerCellY;
  //   
  //   // Check if more than 50% of item is outside grid bounds (all in pixels now)
  //   const itemLeft = itemX;
  //   const itemRight = itemX + itemWidthPx;
  //   const itemTop = itemY;
  //   const itemBottom = itemY + itemHeightPx;

  //   // Calculate overlap with grid (all in pixels)
  //   const overlapLeft = Math.max(0, Math.min(itemRight, gridRect.width) - Math.max(itemLeft, 0));
  //   const overlapTop = Math.max(0, Math.min(itemBottom, gridRect.height) - Math.max(itemTop, 0));
  //   const overlapArea = overlapLeft * overlapTop;

  //   // Calculate total item area in pixels
  //   const itemArea = itemWidthPx * itemHeightPx;

  //   // Check if less than 50% is inside (more than 50% outside)
  //   const hasViolation = overlapArea < (itemArea * 0.5);

  //   setGridHasViolation(hasViolation);
  //   
  //   // Show warning banner when there's a violation
  //   if (hasViolation) {
  //     setShowBoundaryWarning(true);
  //     setBoundaryWarningMessage('La mesa está más del 50% fuera del área del salón');
  //     
  //     // Auto-hide after 3 seconds
  //     setTimeout(() => {
  //       setShowBoundaryWarning(false);
  //     }, 3000);
  //   }
  // }, [selectedItem, pxPerCellX, pxPerCellY, actionType]);

  // Detectar móvil y tablet
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sincronizar referencia de selectedSalon
  useEffect(() => {
    if (!selectedSalon) return;
    const updated = salones.find(s => s.salon_id === selectedSalon.salon_id);
    if (updated && updated !== selectedSalon) {
      setSelectedSalon(updated);
    }
  }, [salones, selectedSalon, setSelectedSalon]);

  const handleUpdateSalones = (mesaId: number, payload: any) => {
    updateSalones(mesaId, payload, setSalones);
  };

  // Manejar teclas de movimiento
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    
    // Get actual grid dimensions from the DOM element
    let gridDims: { maxWidth: number; maxHeight: number } | undefined = undefined;
    if (gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect();
      gridDims = {
        maxWidth: rect.width,
        maxHeight: rect.height,
      };
    }
    const itemDims = selectedItem
      ? { width: selectedItem.width, height: selectedItem.height }
      : null;
    handleKeyPressRaw(
      event,
      selectedItem,
      selectedSalon,
      setSelectedItem,
      setSelectedSalon,
      salones,
      setSalones,
      gridRef.current,
      gridDims,
      itemDims,
      pxPerCellX,
      pxPerCellY,
      scaleFactor,
      setShowBoundaryWarning,
      setGridHasViolation
    );
  }, [
    selectedItem,
    selectedSalon,
    setSelectedItem,
    setSelectedSalon,
    salones,
    setSalones,
    pxPerCellX,
    pxPerCellY,
    setShowBoundaryWarning,
    setGridHasViolation,
    actionType
  ]);

  // Solo escuchamos keydown; NO volvemos a setScaleFactor aquí
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // gridStyle: full scale en móvil, en desktop mantiene tu cálculo original (2/3)
  const gridStyle = isMobile
    ? {
        width:     `calc(900px * ${scaleFactor})`,
        height:    `calc(950px * ${scaleFactor})`,
        maxWidth:  `${580 * scaleFactor}px`,
        maxHeight: `${560 * scaleFactor}px`,
      }
    : {
        width:     `calc(900px * ${scaleFactor * 2/3})`,
        height:    `calc(950px * ${scaleFactor * 2/3})`,
        maxWidth:  `${580 * scaleFactor}px`,
        maxHeight: `${560 * scaleFactor}px`,
      };

  // Abrir modal de mesa en modo restaurant
  useEffect(() => {
    if (actionType === 'restaurant' && selectedItem && 'num' in selectedItem) {
      setShowMesaModal(true);
    }
  }, [actionType, selectedItem]);

  return (
    <div className="salon-displayer">
      {/* Boundary Warning Banner */}
      {/* COMMENTED OUT: 50% boundary checking disabled - needs fixing */}
      {/* {showBoundaryWarning && actionType === 'configuration' && (
        <div className="boundary-warning-banner">
          <div className="warning-content">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">
              {boundaryWarningMessage || 'No se puede mover más del 50% del elemento fuera del área del salón'}
            </div>
          </div>
        </div>
      )} */}

      {/* Movement Instructions Banner */}
      {selectedItem && actionType === 'configuration' && (
        <div className="movement-instructions-banner">
          <div className="instructions-content">
            <div className="instructions-icon">🎯</div>
            <div className="instructions-text">
              <span className="instructions-main">
                {'num' in selectedItem ? `Mesa ${selectedItem.num}` : selectedItem.name} seleccionada
              </span>
              <span className="instructions-sub">
                Usa las teclas de flecha <kbd>↑</kbd> <kbd>↓</kbd> <kbd>←</kbd> <kbd>→</kbd> para mover
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div className="content-wrapper">
        {/* COMMENTED OUT grid-violation class: ${gridHasViolation && actionType === 'configuration' ? 'grid-violation' : ''} */}
        <div
          ref={gridRef}
          className="grid"
          style={gridStyle}
          onDragOver={actionType === 'configuration' ? e => e.preventDefault() : undefined}
          onDrop={actionType === 'configuration' ? (e) => handleDrop(e, selectedSalon, setSelectedSalon, scaleFactor, displayBoundaryWarning) : undefined}
        >
          <RenderMesas
            salon={selectedSalon}
            actionType={actionType}
            scaleFactor={scaleFactor}
            onDragStart={actionType === 'configuration' ? handleDragStart : () => {}}
            setSelectedItem={setSelectedItem}
            selectedItem={selectedItem}
            isPedidos={isPedidos}
            mergedMesas={mergedMesas}
            mergeMode={mergeMode}
            setMergedMesas={setMergedMesas}
            filteredReservationsInAHour={filteredReservationsInAHour}
            setPxPerCellX={setPxPerCellX}
            setPxPerCellY={setPxPerCellY}
            displayPrefs={displayPrefs}
          />
          <RenderObjetos
            objetos={selectedSalon?.objetos || []}
            actionType={actionType}
            scaleFactor={scaleFactor}
            onDragStart={actionType === 'configuration' ? handleDragStart : () => {}}
            setSelectedItem={setSelectedItem}
            selectedItem={selectedItem}
            setPxPerCellX={setPxPerCellX}
            setPxPerCellY={setPxPerCellY}
          />
        </div>

        {actionType === 'configuration' && selectedItem && selectedSalon && (
          isMobile ? (
            <ItemControllerMobile
              show
              handleClose={() => setSelectedItem(null)}
              salones={salones}
              setSalones={setSalones}
              selectedItem={selectedItem}
              selectedSalon={selectedSalon}
              setSelectedSalon={setSelectedSalon}
              setSelectedItem={setSelectedItem}
              updateSalon={setSelectedSalon}
              updateSelectedItem={setSelectedItem}
            />
          ) : (
            <ItemControllers
              salones={salones}
              setSalones={setSalones}
              selectedItem={selectedItem}
              selectedSalon={selectedSalon}
              setSelectedSalon={setSelectedSalon}
              setSelectedItem={setSelectedItem}
              updateSalon={setSelectedSalon}
              updateSelectedItem={setSelectedItem}
            />
          )
        )}

        {showMesaModal && selectedItem && 'num' in selectedItem && selectedItem.state && !hosterMode && (
          <MesaController
            mesa={selectedItem as Mesa}
            show={showMesaModal}
            onClose={() => {
              setShowMesaModal(false);
              setSelectedItem(null);
            }}
            onMesaClosed={(mesaId) => {
              // Update salones state to reflect mesa closure in real-time
              console.log('[SalonDisplayer] Mesa closed callback received for mesa:', mesaId);
              handleUpdateSalones(mesaId, { event: 'table_closed', data: { mesa_id: mesaId } });
            }}
          />
        )}
      </div>

      <PedidosListener
        setPedidos={() => {}}
        pedido_type_id={1}
        updateSalones={handleUpdateSalones}
      />
    </div>
  );
};

export default SalonesDisplayer;
